import { useOpenModal } from '@/hooks';
import { useLayoutStorage } from '@/stores';
import { ComponentType, GenericWidgetProps } from '@/types.ts';
import { CHART_COMPONENT_NAME } from '@/widgetComponentNames.ts';

import { AggregatedChartCard } from './chart/AggregatedChartCard.tsx';
import { BoxPlotCard } from './chart/BoxPlotCard.tsx';
import { HistogramCard } from './chart/HistogramCard.tsx';
import { LinearChartCard } from './chart/LinearChartCard.tsx';
import { ScatterPlotCard } from './chart/ScatterPlotCard.tsx';
import { ChartModal } from './ChartModal.tsx';
import {
  AggregatedChartConfigType,
  BoxPlotChartConfigType,
  ChartConfigType,
  HistogramChartConfigType,
  LinearChartConfigType,
  PlotType,
  ScatterPlotChartConfigType,
} from './types.ts';

interface ChartProps extends GenericWidgetProps {
  config?: ChartConfigType;
  hideEditChart?: boolean;
}

/**
 * Set config or use sessionStorage via useLayoutStorage. Note that chart editing only works for charts that are widgets in a layout.
 * In other cases, `hideEditChart` should be set, and PlotType-specific configs used.
 * @param widgetId
 * @param config
 * @param hideEditChart
 * @constructor
 */
export const Chart: ComponentType<ChartProps> = ({
  config,
  hideEditChart,
  widgetId,
}: ChartProps) => {
  const modalController = useOpenModal();

  const getWidgetConfigProps = useLayoutStorage(s => s.getWidgetConfigProps);

  const chartConfig = widgetId ? (config ?? getWidgetConfigProps(widgetId)) : config;

  const chartType = chartConfig?.plotType;

  return (
    <>
      {/*This is default to account for old chart configs with missing PlotType*/}
      {(!chartType || chartType === PlotType.LINEAR) && (
        <LinearChartCard
          id={widgetId}
          onOpenModal={modalController.handleOpenModal}
          config={chartConfig as LinearChartConfigType | undefined}
          hideEditChart={hideEditChart}
        />
      )}
      {chartType === PlotType.AGGREGATED && (
        <AggregatedChartCard
          id={widgetId}
          onOpenModal={modalController.handleOpenModal}
          config={chartConfig as AggregatedChartConfigType | undefined}
          hideEditChart={hideEditChart}
        />
      )}
      {chartType === PlotType.BOXPLOT && (
        <BoxPlotCard
          id={widgetId}
          onOpenModal={modalController.handleOpenModal}
          config={chartConfig as BoxPlotChartConfigType | undefined}
          hideEditChart={hideEditChart}
        />
      )}
      {chartType === PlotType.SCATTER && (
        <ScatterPlotCard
          id={widgetId}
          onOpenModal={modalController.handleOpenModal}
          config={chartConfig as ScatterPlotChartConfigType | undefined}
          hideEditChart={hideEditChart}
        />
      )}
      {chartType === PlotType.HISTOGRAM && (
        <HistogramCard
          id={widgetId}
          onOpenModal={modalController.handleOpenModal}
          config={chartConfig as HistogramChartConfigType | undefined}
          hideEditChart={hideEditChart}
        />
      )}
      <ChartModal modalController={modalController} plotType={chartType} widgetId={widgetId} />
    </>
  );
};

// ✅ Static property
Chart.componentName = CHART_COMPONENT_NAME;
